export const VEHICLE_MAKES = [
  // ── Top 10 (shown as pills on category pages via .slice(0,10)) ──
  "Toyota",
  "Nissan",
  "Honda",
  "Hyundai",
  "Kia",
  "Mercedes-Benz",
  "BMW",
  "Ford",
  "Mitsubishi",
  "Volkswagen",
  // ── Japanese (continued) ──
  "Suzuki",
  "Mazda",
  "Subaru",
  "Lexus",
  "Isuzu",
  "Daihatsu",
  "Acura",
  "Infiniti",
  "Hino",
  // ── Chinese (fastest-growing segment in Ghana) ──
  // Note: "Howo" removed, Howo IS Sinotruk's truck line, not a separate make.
  // Listings tagged make="Howo" should be retagged make="Sinotruk" model="Howo XX".
  // The search-synonyms map below routes "howo" → "sinotruk" so existing buyer
  // searches still work.
  "Great Wall",
  "Haval",
  "Geely",
  "MG",
  "Jetour",
  "Foton",
  "Dongfeng",
  "BYD",
  "Changan",
  "Chery",
  "Sinotruk",
  // ── European (continued) ──
  "Audi",
  "Volvo",
  "Opel",
  "Peugeot",
  "Renault",
  "Land Rover",
  "Jaguar",
  "Mini",
  "Porsche",
  "Scania",
  "DAF",
  // ── American (continued) ──
  "Chevrolet",
  "Jeep",
  // ── Other ──
  "Daewoo",
  "Tata",
  "MAN",
  "Iveco",
];

/**
 * Multi-word make aliases. Same table mirrored in server/vehicle-extract.ts'
 * parseVehicleFromQuery (kept duplicated rather than imported because the
 * server file is large and we want this minimal client helper standalone).
 * Update both if you add new entries.
 *
 * Each entry: [tokens, canonical name]. Tokens are lowercase, punctuation
 * stripped, matched against the typed query word-by-word.
 */
export const MULTI_WORD_MAKES: ReadonlyArray<
  readonly [readonly string[], string]
> = [
  [["land", "rover"], "Land Rover"],
  [["landrover"], "Land Rover"],
  [["range", "rover"], "Land Rover"],
  [["mercedes-benz"], "Mercedes-Benz"],
  [["mercedes", "benz"], "Mercedes-Benz"],
  [["man", "truck"], "MAN"],
] as const;

/**
 * Cheap client-side make detector. Returns the canonical make string when
 * the query mentions one (e.g., "iveco daily" → "Iveco"), else null.
 *
 * Mirrors the make-detection logic in parseVehicleFromQuery so the client
 * and server agree on what counts as a make. Does NOT extract model, year,
 * or build a residual — Products.tsx only needs to know "did the user
 * mention a different make?" so we don't pull the heavier server parser
 * into the client bundle.
 */
export function detectMakeInQuery(query: string): string | null {
  if (!query || !query.trim()) return null;
  const lowerTokens = query.toLowerCase().split(/\s+/);

  // Multi-word first (so "land rover" isn't mis-matched as nothing).
  for (const [seq, canonical] of MULTI_WORD_MAKES) {
    for (let i = 0; i <= lowerTokens.length - seq.length; i++) {
      let ok = true;
      for (let j = 0; j < seq.length; j++) {
        const tok = lowerTokens[i + j].replace(/[^a-z0-9-]/g, "");
        if (tok !== seq[j]) {
          ok = false;
          break;
        }
      }
      if (ok) return canonical;
    }
  }

  // Single-word from VEHICLE_MAKES. Build a lowercase index so the lookup
  // is O(1) rather than O(makes) per token.
  const makesIndex = new Map<string, string>();
  for (const m of VEHICLE_MAKES) makesIndex.set(m.toLowerCase(), m);
  for (const tok of lowerTokens) {
    const stripped = tok.replace(/[^a-z0-9-]/g, "");
    const canonical = makesIndex.get(stripped);
    if (canonical) return canonical;
  }
  return null;
}

export const GHANA_REGIONS = [
  "Greater Accra",
  "Ashanti",
  "Western",
  "Eastern",
  "Central",
  "Northern",
  "Volta",
  "Upper East",
  "Upper West",
  "Brong-Ahafo",
  "Western North",
  "Ahafo",
  "Bono East",
  "Oti",
  "North East",
  "Savannah",
];

export const GHANA_CITIES = [
  "Accra",
  "Ashaiman",
  "Berekum",
  "Bolgatanga",
  "Cape Coast",
  "Dormaa Ahenkro",
  "Dunkwa",
  "Ejura",
  "Goaso",
  "Ho",
  "Kasoa",
  "Koforidua",
  "Konongo",
  "Kumasi",
  "Nkawkaw",
  "Obuasi",
  "Sefwi Wiawso",
  "Sunyani",
  "Takoradi",
  "Tamale",
  "Tarkwa",
  "Techiman",
  "Tema",
  "Wa",
  "Winneba",
];

// Auto-derive region from city on vendor signup. Reduces the Step 2 form
// from two manual dropdowns to one in the common case — vendors can still
// override the derived region if they need to. Falls back to "Brong-Ahafo"
// for Bono-cluster cities (Berekum, Dormaa Ahenkro, Sunyani) because that
// region name is the one currently present in GHANA_REGIONS; Bono (the
// post-2018-split successor region) is not yet in the list.
export const CITY_TO_REGION: Record<string, string> = {
  Accra: "Greater Accra",
  Ashaiman: "Greater Accra",
  Berekum: "Brong-Ahafo",
  Bolgatanga: "Upper East",
  "Cape Coast": "Central",
  "Dormaa Ahenkro": "Brong-Ahafo",
  Dunkwa: "Central",
  Ejura: "Ashanti",
  Goaso: "Ahafo",
  Ho: "Volta",
  Kasoa: "Central",
  Koforidua: "Eastern",
  Konongo: "Ashanti",
  Kumasi: "Ashanti",
  Nkawkaw: "Eastern",
  Obuasi: "Ashanti",
  "Sefwi Wiawso": "Western North",
  Sunyani: "Brong-Ahafo",
  Takoradi: "Western",
  Tamale: "Northern",
  Tarkwa: "Western",
  Techiman: "Bono East",
  Tema: "Greater Accra",
  Wa: "Upper West",
  Winneba: "Central",
};

// Vendor-specialty checkboxes used on /vendor/register Step 2 in place of a
// free-text "What do you specialise in?" textarea. Selecting categories is
// 2–3 taps instead of typing a sentence on a 5″ screen over 3G; the vendor
// signup mutation concatenates the selected labels into the description
// field so the storefront still gets a human-readable summary.
export const VENDOR_SPECIALTY_CATEGORIES = [
  "Engine & Transmission",
  "Brakes & Suspension",
  "Body & Exterior",
  "Electrical & Battery",
  "Tyres & Wheels",
  "Fluids & Filters",
  "Interior & Accessories",
  "Tools & Equipment",
] as const;

export const PART_CONDITIONS = [
  { value: "new", label: "Brand New" },
  { value: "used", label: "Used / Tokunbo" },
  { value: "refurbished", label: "Refurbished" },
];

export const ORDER_STATUSES = [
  { value: "pending", label: "Pending", color: "yellow" },
  { value: "confirmed", label: "Confirmed", color: "blue" },
  { value: "processing", label: "Processing", color: "indigo" },
  { value: "shipped", label: "Shipped", color: "purple" },
  { value: "delivered", label: "Delivered", color: "green" },
  { value: "cancelled", label: "Cancelled", color: "red" },
];

// Valid order status transitions, key is current status, value is allowed next statuses
export const VALID_STATUS_TRANSITIONS: Record<string, string[]> = {
  pending: ["confirmed", "cancelled"],
  confirmed: ["processing", "cancelled"],
  processing: ["shipped", "cancelled"],
  shipped: ["delivered"],
  delivered: [],
  cancelled: [],
};

export function isValidStatusTransition(from: string, to: string): boolean {
  return VALID_STATUS_TRANSITIONS[from]?.includes(to) ?? false;
}

export const PAYMENT_METHODS = [
  {
    value: "mobile_money",
    label: "Mobile Money (MTN MoMo, Vodafone Cash)",
    icon: "Smartphone",
  },
  {
    value: "card",
    label: "Card Payment (Visa / Mastercard)",
    icon: "CreditCard",
  },
  { value: "pay_on_delivery", label: "Pay on Delivery", icon: "Banknote" },
] as const;

export type PaymentMethod = "pay_on_delivery" | "mobile_money" | "card";

export const MOMO_PROVIDERS = [
  {
    value: "mtn",
    label: "MTN Mobile Money",
    prefix: ["024", "054", "055", "059"],
  },
  { value: "vod", label: "Vodafone Cash", prefix: ["020", "050"] },
  {
    value: "tgo",
    label: "AirtelTigo Money",
    prefix: ["026", "027", "056", "057"],
  },
] as const;
export type PaymentStatus = "unpaid" | "paid" | "refunded";

/**
 * MoMo network identifiers used across PawaPay, Flutterwave and checkout UI.
 * Matches `momoNetworkEnum` in drizzle/schema.ts.
 */
export type MoMoNetwork = "mtn_momo" | "telecel" | "airteltigo";

/**
 * Vendor subscription tier. Matches `vendorTierEnum` in drizzle/schema.ts.
 * Use this union everywhere a tier is referenced in business logic.
 */
export type VendorTier = "free" | "starter" | "pro" | "business" | "enterprise";

/** Paid tier names only, excludes "free". Used for subscription pricing maps. */
export type PaidVendorTier = Exclude<VendorTier, "free">;

/** Subscription billing cadence. */
export type BillingPeriod = "monthly" | "annual";

/**
 * Canonical tier pricing in GHS (whole units, not pesewas).
 * Single source of truth, import from pawapay, flutterwave, routers, UI.
 */
export const TIER_PRICES: Record<
  PaidVendorTier,
  { monthly: number; annual: number }
> = {
  starter: { monthly: 100, annual: 1000 },
  pro: { monthly: 200, annual: 2000 },
  business: { monthly: 800, annual: 8000 },
  enterprise: { monthly: 2000, annual: 20000 },
} as const;

/** Featured vendor boost price (GHS/week). */
export const BOOST_PRICE_WEEKLY = 50;

/** Trust tier derived from vendor quality score + verification flags. */
export type TrustTier = "verified" | "active" | "unverified";

/** Vendor dashboard product sort options. */
export type ProductSortKey =
  | "newest"
  | "oldest"
  | "price_asc"
  | "price_desc"
  | "views"
  | "low_stock";

/** Vendor dashboard order filter options, matches orderStatusEnum + "all". */
export type OrderFilter =
  | "all"
  | "pending"
  | "confirmed"
  | "processing"
  | "shipped"
  | "delivered"
  | "cancelled";

// Ghana phone number validation, accepts 0XX, 233XX, +233XX, or +2330XX (common mistake)
export function isValidGhanaPhone(phone: string): boolean {
  // Strip whitespace, dashes, parens, plus sign
  let cleaned = phone.replace(/[\s\-()]/g, "").replace(/^\+/, "");
  // Normalise: remove redundant leading 0 after country code (e.g. 2330241... → 233241...)
  cleaned = cleaned.replace(/^2330([2-9])/, "233$1");
  return /^0[2-9]\d{8}$/.test(cleaned) || /^233[2-9]\d{8}$/.test(cleaned);
}

/**
 * Canonicalise a Ghana phone number for equality comparison.
 *
 * Ghana phones reach the DB in many forms:
 *   "0241234567", "+233241234567", "233241234567", "+2330241234567",
 *   "024 123 4567", "(024) 123-4567"
 *
 * All of those represent the same subscriber. normalizePhone returns the
 * bare 9-digit local number (e.g. "241234567") so two phones can be compared
 * regardless of how they were originally typed.
 *
 * Returns null for inputs that don't parse as a valid Ghana number.
 */
export function normalizePhone(
  phone: string | null | undefined
): string | null {
  if (!phone) return null;
  // Strip whitespace, dashes, parens, plus sign
  let cleaned = phone.replace(/[\s\-()]/g, "").replace(/^\+/, "");
  // Fix "+2330XX" → "233XX" (common typing mistake)
  cleaned = cleaned.replace(/^2330([2-9])/, "233$1");
  // Drop country code
  if (/^233[2-9]\d{8}$/.test(cleaned)) return cleaned.slice(3);
  // Drop leading zero
  if (/^0[2-9]\d{8}$/.test(cleaned)) return cleaned.slice(1);
  // Already bare 9-digit local number?
  if (/^[2-9]\d{8}$/.test(cleaned)) return cleaned;
  return null;
}

/**
 * Canonical E.164 form of a Ghana phone number ("+233XXXXXXXXX").
 *
 * Layered on top of normalizePhone: any of the accepted shapes
 * ("0241234567", "+233241234567", "233241234567", "024 123 4567" etc.)
 * round-trips to exactly "+233241234567". Returns null for inputs that
 * don't parse as a valid Ghana number — callers MUST handle null rather
 * than silently truncating (the prior client-side helper sliced extra
 * digits, which sent OTPs to the wrong subscriber).
 *
 * Twilio Verify (`To=…`) requires E.164. The server's OTP procedures
 * call this helper before every Verify request so a "0241234567" body
 * doesn't reach Twilio as a non-E.164 string (which Twilio rejects with
 * a 400 the user perceives as a generic "couldn't send" failure).
 */
export function toE164GhanaPhone(
  phone: string | null | undefined
): string | null {
  const local = normalizePhone(phone);
  if (!local) return null;
  return `+233${local}`;
}

/**
 * Mask a phone number for logs/Sentry. Returns a string that preserves
 * the country code and last 4 digits, replacing the middle with stars.
 * Examples:
 *   "+233241234567" → "+233****4567"
 *   "0241234567"    → "+233****4567" (round-trips through E.164)
 *   ""              → "<no-phone>"
 *   null            → "<no-phone>"
 *
 * Used at every Twilio catch site so OTP failure reports do not store
 * raw PII in error tracking. Mirrors the server-side beforeSend rule in
 * server/_core/index.ts which also masks `request.data.phone`.
 */
export function maskPhone(phone: string | null | undefined): string {
  if (!phone) return "<no-phone>";
  const e164 = toE164GhanaPhone(phone);
  if (e164) {
    // "+233" + 9 digits — preserve country code + last 4 digits.
    return e164.slice(0, 4) + "****" + e164.slice(-4);
  }
  // Non-Ghana / unparseable: keep only the last 4 chars worth of digits.
  const digits = phone.replace(/\D/g, "");
  if (digits.length <= 4) return "****";
  return "****" + digits.slice(-4);
}

/**
 * L3: Canonical Ghana Cedi symbol. Previously hardcoded as `GH₵` in 9+
 * components, if Ghana ever redesignates the symbol we only have to
 * change it here.
 */
export const CURRENCY = "GH₵";

export function formatGHS(amount: string | number): string {
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  if (isNaN(num)) return `${CURRENCY}0`;
  const hasDecimals = num % 1 !== 0;
  return `${CURRENCY}${num.toLocaleString("en-GH", {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * P6: Shared upper bound on vehicle fitments per product. Must match the
 * server-side Zod schema in `server/routers.ts`. Importing from one place
 * prevents the client and server from drifting.
 */
export const MAX_PRODUCT_FITMENTS = 50;

export function generateWhatsAppLink(phone: string, message: string): string {
  const cleanPhone = phone.replace(/[^0-9]/g, "");
  // If original number had a + prefix it already carries a country code, use as-is
  const hasCountryCode = phone.trim().startsWith("+");
  let finalPhone: string;
  if (hasCountryCode) {
    finalPhone = cleanPhone;
  } else if (cleanPhone.startsWith("0")) {
    finalPhone = `233${cleanPhone.slice(1)}`;
  } else if (cleanPhone.startsWith("233")) {
    finalPhone = cleanPhone;
  } else {
    finalPhone = `233${cleanPhone}`;
  }
  return `https://wa.me/${finalPhone}?text=${encodeURIComponent(message)}`;
}

// ─── WhatsApp prefilled message templates ──────────────────────────────────
//
// Single source of truth for the messages buyers send vendors. Previously
// each call site wrote its own ("Hi, I'm interested..." / "Hi, I saw..." /
// "Hi! I saw..." with mixed brand mentions and an inquire/enquire split).
// Centralising keeps the voice consistent: one greeting, one brand mention,
// UK spelling ("enquire") which fits Ghanaian English convention.

export interface ProductForWhatsApp {
  name: string;
  price: string | number;
}

export interface VendorForWhatsApp {
  businessName: string;
}

export interface PartRequestForWhatsApp {
  partName: string;
  /** Optional vehicle context, e.g. "Toyota Corolla 2015" */
  vehicleStr?: string;
}

/** Buyer enquiring about an in-stock product. */
export function productEnquiryMessage(
  product: ProductForWhatsApp,
  formattedPrice: string
): string {
  return `Hi, I want to buy your "${product.name}" for ${formattedPrice} on Voom. Do you still have it?`;
}

/** Buyer asking when an out-of-stock product will be back. */
export function productRestockEnquiryMessage(
  product: ProductForWhatsApp,
  formattedPrice: string
): string {
  return `Hi, your "${product.name}" (${formattedPrice}) on Voom is sold out. When will you have it again?`;
}

/** Buyer reaching out to a vendor's shop page (no specific product). */
export function vendorShopEnquiryMessage(vendor: VendorForWhatsApp): string {
  return `Hi ${vendor.businessName}, I found your shop on Voom Parts Inc. I'd like to enquire about your parts.`;
}

/** Vendor responding to a buyer's open part request on the request board. */
export function partRequestFulfilmentMessage(
  request: PartRequestForWhatsApp
): string {
  const vehicle = request.vehicleStr ? ` (${request.vehicleStr})` : "";
  return `Hi, I saw your request for "${request.partName}"${vehicle} on Voom Parts Inc. I may have what you need. Is this still open?`;
}

// ─── Algorithm Intelligence Constants ─────────────────────────────────────────

/** Ghana fleet composition by make, used for ranking weight.
 *  Source: Jiji.com.gh listing volumes + DVLA registration data, April 2026.
 *  Chinese brands are the fastest-growing segment so they get meaningful
 *  weight even if absolute fleet share is still small. */
export const FLEET_WEIGHTS: Record<string, number> = {
  // ── Top 5 (Japanese & Korean dominance) ──
  Toyota: 0.33,
  Honda: 0.21,
  Hyundai: 0.11,
  Nissan: 0.08,
  Kia: 0.03,
  // ── German luxury / European volume ──
  "Mercedes-Benz": 0.06,
  BMW: 0.01,
  Volkswagen: 0.005,
  Audi: 0.008,
  // ── American ──
  Ford: 0.02,
  Chevrolet: 0.02,
  Dodge: 0.005,
  Jeep: 0.005,
  // ── Premium British/European ──
  "Land Rover": 0.015,
  Lexus: 0.01,
  Jaguar: 0.005,
  Porsche: 0.003,
  Mini: 0.003,
  // ── Other Japanese ──
  Mitsubishi: 0.025,
  Mazda: 0.01,
  Subaru: 0.005,
  Suzuki: 0.005,
  Daihatsu: 0.005,
  Acura: 0.004,
  Infiniti: 0.004,
  Hino: 0.005,
  // ── Chinese (fastest-growing, over-weight relative to current share to
  //    push these up in ranking as the market shifts) ──
  Haval: 0.015,
  Geely: 0.012,
  MG: 0.01,
  Jetour: 0.008,
  "Great Wall": 0.008,
  BYD: 0.008,
  Chery: 0.006,
  Foton: 0.005,
  Changan: 0.005,
  Dongfeng: 0.004,
  // ── Commercial trucks ──
  Sinotruk: 0.005,
  Scania: 0.004,
  DAF: 0.003,
  MAN: 0.003,
  Iveco: 0.003,
  Isuzu: 0.005,
  // ── Other ──
  Peugeot: 0.005,
  Renault: 0.003,
  Volvo: 0.003,
  Opel: 0.003,
  Daewoo: 0.002,
  Tata: 0.002,
};

/** Returns fleet weight for a make (0.0–0.33). Unknown makes → 0.005 */
export function getFleetWeight(make: string | null | undefined): number {
  if (!make) return 0;
  return FLEET_WEIGHTS[make] ?? 0.005;
}

/**
 * Canonical "popular makes" list, top 12 by Ghana fleet weight. Used by the
 * Products page pill bar, Home page chip strip (sliced to 8 for iPhone-SE
 * width), the footer "Parts by Make" column, and the Category detail page.
 * Sourced from FLEET_WEIGHTS so a weight change reranks all four surfaces
 * automatically.
 */
export const POPULAR_MAKES: readonly string[] = Object.entries(FLEET_WEIGHTS)
  .sort(([, a], [, b]) => b - a)
  .map(([make]) => make)
  .slice(0, 12);

/** Seasonal demand tiers, maps category IDs to rainy/dry season relevance.
 *  TIER 1 = critical surge (rainy season April-June)
 *  TIER 2 = high demand (rainy + transition July-Aug)
 *  TIER 3 = steady year-round */
export const SEASONAL_TIERS = {
  TIER_1_RAINY: [2, 9, 4] as number[], // Brakes/Suspension(2), Wheels/Tires(9), Lights(4)
  TIER_2_HIGH: [8, 5, 6] as number[], // Electrical/Battery(8), Cooling(5), Filters(6)
  TIER_3_STEADY: [1, 3, 7, 10, 11, 12] as number[], // Engine, Body, Trans, Interior, Audio, Security
} as const;

/** Get seasonal multiplier for a category based on current month */
export function getSeasonalMultiplier(
  categoryId: number | null,
  month?: number
): number {
  if (!categoryId) return 1.0;
  const m = month ?? new Date().getMonth() + 1; // 1-12
  const isRainy = m >= 4 && m <= 6; // April-June
  const isTransition = m >= 7 && m <= 8; // July-August

  if (isRainy && SEASONAL_TIERS.TIER_1_RAINY.includes(categoryId)) return 1.5;
  if (isRainy && SEASONAL_TIERS.TIER_2_HIGH.includes(categoryId)) return 1.2;
  if (isTransition && SEASONAL_TIERS.TIER_2_HIGH.includes(categoryId))
    return 1.3;
  if (isTransition && SEASONAL_TIERS.TIER_1_RAINY.includes(categoryId))
    return 1.2;
  return 1.0;
}

/** Top 20 models by demand, used for model-level ranking boost.
 *  Weight 0.0–1.0 where 1.0 = highest demand (Corolla) */
export const MODEL_DEMAND: Record<string, Record<string, number>> = {
  Toyota: {
    Corolla: 1.0,
    Camry: 0.85,
    Hilux: 0.75,
    "Land Cruiser": 0.7,
    Yaris: 0.6,
    RAV4: 0.55,
    HiAce: 0.5,
    Prado: 0.65,
    Vitz: 0.6,
    Highlander: 0.4,
    Fortuner: 0.35,
    Venza: 0.3,
  },
  Honda: {
    Civic: 0.8,
    Accord: 0.65,
    "CR-V": 0.6,
    Fit: 0.5,
    Pilot: 0.3,
    "HR-V": 0.25,
  },
  Hyundai: {
    Elantra: 0.9,
    Accent: 0.7,
    Tucson: 0.55,
    Sonata: 0.45,
    Kona: 0.3,
    i10: 0.35,
  },
  Nissan: {
    Sunny: 0.5,
    Navara: 0.55,
    "X-Trail": 0.45,
    Note: 0.35,
    Pathfinder: 0.3,
    Almera: 0.4,
  },
  Kia: { Picanto: 0.6, Sportage: 0.45, Rio: 0.4, Sorento: 0.3 },
  Mitsubishi: { Pajero: 0.5, L200: 0.35, Outlander: 0.25 },
  "Mercedes-Benz": { "C-Class": 0.5, "E-Class": 0.4, Sprinter: 0.3 },
  Ford: { Ranger: 0.4, "F-150": 0.35, Escape: 0.3, Explorer: 0.25 },
  // Chinese brands (rapidly growing in Ghana)
  "Great Wall": { Poer: 0.5, Wingle: 0.35 },
  Haval: { Jolion: 0.6, H6: 0.4, H9: 0.3 },
  Geely: { Coolray: 0.55, Emgrand: 0.3 },
  MG: { ZS: 0.5, HS: 0.35, "5": 0.25 },
  Jetour: { T2: 0.5, X70: 0.35, Dashing: 0.3 },
  Foton: { Tunland: 0.4, Aumark: 0.35, Auman: 0.3 },
  Dongfeng: { Rich: 0.35, AX7: 0.3 },
  BYD: { "Atto 3": 0.4, Dolphin: 0.35, Seal: 0.3 },
  Changan: { CS75: 0.35, Eado: 0.25 },
  Chery: { "Tiggo 4": 0.4, "Tiggo 7": 0.35, "Tiggo 8": 0.3 },
  // Additional Japanese
  Daihatsu: { Mira: 0.4, Terios: 0.35, Hijet: 0.3 },
  Acura: { TL: 0.4, MDX: 0.35, TSX: 0.3, RDX: 0.25 },
  Infiniti: { FX35: 0.35, G35: 0.3, QX60: 0.25 },
  Hino: { "300": 0.4, "500": 0.35, "700": 0.3 },
  // Additional European
  Jaguar: { XF: 0.3, "F-Pace": 0.25 },
  Mini: { Cooper: 0.3, Countryman: 0.25 },
  Porsche: { Cayenne: 0.35, Panamera: 0.25 },
  Scania: { "P-Series": 0.3, "G-Series": 0.25, "R-Series": 0.2 },
  DAF: { CF: 0.3, XF: 0.25, LF: 0.2 },
  // Commercial Chinese (Howo is Sinotruk's truck line, not a separate make)
  Sinotruk: { "Howo A7": 0.4, "Howo T7H": 0.35, Hohan: 0.3 },
};

/** Get model demand weight (0.0–1.0). Unknown model → 0.1 */
export function getModelDemand(
  make: string | null | undefined,
  model: string | null | undefined
): number {
  if (!make || !model) return 0;
  return MODEL_DEMAND[make]?.[model] ?? 0.1;
}

/** Price bands per part category (GHS), keyed by category ID.
 *  Each band has make-specific overrides for premium vs economy parts. */
export const PRICE_BANDS: Record<
  number,
  {
    keyword: string;
    base: number;
    premiumMakes: string[];
    premiumMultiplier: number;
  }[]
> = {
  2: [
    // Brakes & Suspension
    {
      keyword: "brake pad",
      base: 400,
      premiumMakes: [
        "Mercedes-Benz",
        "BMW",
        "Lexus",
        "Land Rover",
        "Porsche",
        "Jaguar",
        "Audi",
      ],
      premiumMultiplier: 2.5,
    },
    {
      keyword: "shock",
      base: 700,
      premiumMakes: [
        "Mercedes-Benz",
        "BMW",
        "Land Rover",
        "Porsche",
        "Jaguar",
        "Audi",
      ],
      premiumMultiplier: 2.0,
    },
    {
      keyword: "strut",
      base: 800,
      premiumMakes: ["Mercedes-Benz", "BMW", "Porsche", "Jaguar", "Audi"],
      premiumMultiplier: 2.0,
    },
    {
      keyword: "ball joint",
      base: 250,
      premiumMakes: ["Mercedes-Benz", "BMW"],
      premiumMultiplier: 2.0,
    },
    {
      keyword: "tie rod",
      base: 200,
      premiumMakes: ["Mercedes-Benz", "BMW"],
      premiumMultiplier: 2.0,
    },
    {
      keyword: "steering rack",
      base: 1500,
      premiumMakes: [
        "Mercedes-Benz",
        "BMW",
        "Lexus",
        "Porsche",
        "Jaguar",
        "Audi",
      ],
      premiumMultiplier: 2.5,
    },
    {
      keyword: "control arm",
      base: 350,
      premiumMakes: ["Mercedes-Benz", "BMW"],
      premiumMultiplier: 2.5,
    },
  ],
  9: [
    // Wheels & Tires
    {
      keyword: "tyre",
      base: 800,
      premiumMakes: ["Mercedes-Benz", "BMW", "Land Rover"],
      premiumMultiplier: 1.8,
    },
    {
      keyword: "tire",
      base: 800,
      premiumMakes: ["Mercedes-Benz", "BMW", "Land Rover"],
      premiumMultiplier: 1.8,
    },
    {
      keyword: "rim",
      base: 2000,
      premiumMakes: ["Mercedes-Benz", "BMW", "Lexus", "Porsche", "Audi"],
      premiumMultiplier: 2.5,
    },
  ],
  4: [
    // Lights
    {
      keyword: "headlight",
      base: 1200,
      premiumMakes: [
        "Mercedes-Benz",
        "BMW",
        "Lexus",
        "Porsche",
        "Jaguar",
        "Audi",
      ],
      premiumMultiplier: 3.0,
    },
    {
      keyword: "tail light",
      base: 600,
      premiumMakes: ["Mercedes-Benz", "BMW", "Lexus"],
      premiumMultiplier: 2.5,
    },
    { keyword: "wiper", base: 80, premiumMakes: [], premiumMultiplier: 1.0 },
    {
      keyword: "fog light",
      base: 400,
      premiumMakes: ["Mercedes-Benz", "BMW"],
      premiumMultiplier: 2.0,
    },
  ],
  8: [
    // Electrical
    { keyword: "battery", base: 950, premiumMakes: [], premiumMultiplier: 1.0 },
    {
      keyword: "alternator",
      base: 800,
      premiumMakes: ["Mercedes-Benz", "BMW"],
      premiumMultiplier: 2.0,
    },
    {
      keyword: "sensor",
      base: 300,
      premiumMakes: ["Mercedes-Benz", "BMW", "Lexus"],
      premiumMultiplier: 2.5,
    },
  ],
  5: [
    // Cooling
    {
      keyword: "radiator",
      base: 900,
      premiumMakes: ["Mercedes-Benz", "BMW"],
      premiumMultiplier: 2.0,
    },
    {
      keyword: "ac compressor",
      base: 1800,
      premiumMakes: ["Mercedes-Benz", "BMW", "Lexus"],
      premiumMultiplier: 2.0,
    },
    {
      keyword: "thermostat",
      base: 200,
      premiumMakes: ["Mercedes-Benz", "BMW"],
      premiumMultiplier: 2.0,
    },
    {
      keyword: "water pump",
      base: 500,
      premiumMakes: ["Mercedes-Benz", "BMW"],
      premiumMultiplier: 2.0,
    },
  ],
  6: [
    // Filters & Fluids
    {
      keyword: "engine oil",
      base: 400,
      premiumMakes: [],
      premiumMultiplier: 1.0,
    },
    {
      keyword: "air filter",
      base: 70,
      premiumMakes: [],
      premiumMultiplier: 1.5,
    },
    {
      keyword: "oil filter",
      base: 50,
      premiumMakes: [],
      premiumMultiplier: 1.5,
    },
  ],
  1: [
    // Engine
    {
      keyword: "engine",
      base: 20000,
      premiumMakes: ["Mercedes-Benz", "BMW", "Lexus"],
      premiumMultiplier: 2.5,
    },
    {
      keyword: "timing belt",
      base: 550,
      premiumMakes: ["Mercedes-Benz", "BMW"],
      premiumMultiplier: 2.0,
    },
  ],
  3: [
    // Exterior & Body
    {
      keyword: "bumper",
      base: 5000,
      premiumMakes: ["Mercedes-Benz", "BMW", "Lexus", "Land Rover"],
      premiumMultiplier: 2.5,
    },
    {
      keyword: "door",
      base: 2500,
      premiumMakes: ["Mercedes-Benz", "BMW"],
      premiumMultiplier: 2.0,
    },
    {
      keyword: "mirror",
      base: 400,
      premiumMakes: ["Mercedes-Benz", "BMW", "Lexus"],
      premiumMultiplier: 3.0,
    },
  ],
};

/** Check price against make-aware band → "good_deal" | "price_check" | null */
export function getPriceBadge(
  productName: string,
  price: number,
  categoryId?: number | null,
  vehicleMake?: string | null
): "good_deal" | "price_check" | null {
  const lower = productName.toLowerCase();

  // Try category-specific bands first
  if (categoryId && PRICE_BANDS[categoryId]) {
    for (const band of PRICE_BANDS[categoryId]) {
      if (lower.includes(band.keyword)) {
        const isPremium = vehicleMake
          ? band.premiumMakes.includes(vehicleMake)
          : false;
        const median = isPremium
          ? band.base * band.premiumMultiplier
          : band.base;
        if (price < median * 0.8) return "good_deal";
        if (price > median * 1.5) return "price_check";
        return null;
      }
    }
  }

  // Fallback: scan all categories
  for (const bands of Object.values(PRICE_BANDS)) {
    for (const band of bands) {
      if (lower.includes(band.keyword)) {
        const isPremium = vehicleMake
          ? band.premiumMakes.includes(vehicleMake)
          : false;
        const median = isPremium
          ? band.base * band.premiumMultiplier
          : band.base;
        if (price < median * 0.8) return "good_deal";
        if (price > median * 1.5) return "price_check";
        return null;
      }
    }
  }
  return null;
}

/** Compute vendor-level trust score. All inputs are vendor-level, not product-level. */
export function computeTrustScore(vendor: {
  phone?: string | null;
  whatsapp?: string | null;
  city?: string | null;
  region?: string | null;
  loginMethod?: string;
  createdAt?: Date | string | null;
  productCount?: number;
  /** Count of vendor's products that have real images (vendor-level, not per-product) */
  productsWithImages?: number;
}): { score: number; tier: TrustTier } {
  let score = 0;
  if (vendor.phone) score += 20;
  if (vendor.whatsapp) score += 15;
  // Images: vendor-level, at least 1 product with images
  if ((vendor.productsWithImages ?? 0) > 0) score += 25;
  if (vendor.city === "Accra" || vendor.region === "Greater Accra") score += 10;
  if (vendor.createdAt) {
    const age = Date.now() - new Date(vendor.createdAt).getTime();
    if (age < 30 * 24 * 60 * 60 * 1000) score += 15;
  }
  if ((vendor.productCount ?? 0) >= 3) score += 10;
  // Voom registered vendors get bonus; Tonaton imports capped at Active
  if (vendor.loginMethod && vendor.loginMethod !== "import") score += 30;

  const isImport = vendor.loginMethod === "import";
  let tier: TrustTier;
  if (isImport) {
    tier = score >= 50 ? "active" : "unverified";
  } else {
    tier = score >= 80 ? "verified" : score >= 50 ? "active" : "unverified";
  }

  return { score, tier };
}
